import { getChatApiRoute } from '@shared/utils/ChatBackendUtils'

/**
 * Gateway calls for the meeting notepad (HIG-5815). The stored document is
 * markdown: it lives in an owner-only Document Object and the finished audio
 * note retains a reference to that same document.
 * Raw `fetch` rather than `v1Client` because the generated OpenAPI types lag
 * new routes — the same trade `use-meeting-follow-ups` makes.
 */

type GetAuthToken = () => Promise<string>

export interface MeetingNotepadServerState {
  markdown: string
}

/** The live session's notepad. Null when the session has none (or on any failure). */
export async function fetchSessionNotepad(
  getAuthToken: GetAuthToken,
  audioSessionId: string,
): Promise<MeetingNotepadServerState | null> {
  const token = await getAuthToken()
  const response = await fetch(getChatApiRoute(`api/v1/audio-sessions/${audioSessionId}/notepad`), {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!response.ok) {
    throw new Error(`Failed to fetch meeting notepad: ${response.status}`)
  }
  const json = (await response.json()) as { markdown?: string | null }
  if (json.markdown == null) {
    return null
  }
  return { markdown: json.markdown }
}

/** The current document linked from a finished meeting's note. Null when it has none. */
export async function fetchSavedNotepad(
  getAuthToken: GetAuthToken,
  audioNoteId: string,
): Promise<{ markdown: string; documentId: string } | null> {
  const token = await getAuthToken()
  const response = await fetch(getChatApiRoute(`api/v1/audio-notes/${audioNoteId}?view=metadata`), {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!response.ok) {
    throw new Error(`Failed to fetch saved meeting notepad: ${response.status}`)
  }
  const json = (await response.json()) as {
    data?: { meetingNotepad?: string; meetingNotepadDocumentId?: string }
  }
  if (json.data?.meetingNotepad == null || !json.data.meetingNotepadDocumentId) {
    return null
  }
  return {
    markdown: json.data.meetingNotepad,
    documentId: json.data.meetingNotepadDocumentId,
  }
}

/**
 * Best-effort write of the live notepad. Failures only cost cross-device
 * freshness — the local copy is already on disk — so they log rather than throw.
 */
export async function pushSessionNotepad(
  getAuthToken: GetAuthToken,
  audioSessionId: string,
  markdown: string,
): Promise<void> {
  try {
    const token = await getAuthToken()
    const response = await fetch(getChatApiRoute(`api/v1/audio-sessions/${audioSessionId}/notepad`), {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ markdown }),
    })
    if (!response.ok) {
      console.warn(`[MeetingNotepad] failed to sync notes to server: ${response.status}`)
    }
  } catch (error) {
    console.warn('[MeetingNotepad] failed to sync notes to server:', error)
  }
}

export async function pushDocument(getAuthToken: GetAuthToken, documentId: string, content: string): Promise<void> {
  try {
    const token = await getAuthToken()
    const response = await fetch(getChatApiRoute(`api/v1/documents/${documentId}`), {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    })
    if (!response.ok) {
      console.warn(`[MeetingNotepad] failed to sync saved notes to server: ${response.status}`)
    }
  } catch (error) {
    console.warn('[MeetingNotepad] failed to sync saved notes to server:', error)
  }
}
